import { css } from '@emotion/react';
import { faRobot } from '@fortawesome/pro-solid-svg-icons';
import { type FC } from 'react';
import { ACCORDION_HEADER_SIZE_LARGE } from '~/_shared/baseComponents/accordion';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { LimitNumberOfLinesComponent } from '~/_shared/components/limitNumberOfLines/limitNumberOfLines.component';
import type { Theme } from '~/_shared/themes/theme.model';
import { useTranslation } from '~/_shared/utils/hooks';
import type { BoundaryGroupPermissions } from '~/store/boundaryGroups/boundaryGroups.selectors';
import { type ActiveBoundaryProps } from './activeBoundary.component';
import { TrialModeWarningComponent } from './trialMode/trialModeWarning.component';

const wrapperStyle = (theme: Theme) => css({
  width: '100%',
  height: ACCORDION_HEADER_SIZE_LARGE,
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  backgroundColor: theme.backgroundColors.highlight,
  border: `solid ${theme.borderColors.activeItem}`,
  borderWidth: '1px 0',
  boxSizing: 'border-box',
  padding: '0px 16px',
});

const primaryHeaderStyle = (theme: Theme) => css({
  boxSizing: 'border-box',
  color: theme.textColors.primary,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  width: '100%',
});

const groupTypeStyle = (theme: Theme) => css({
  color: theme.textColors.secondary,
  display: 'flex',
  gap: 4,
});

const subTitleStyle = css({
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  fontSize: '12px',
});

type ActiveBoundaryHeaderProps =
  Pick<ActiveBoundaryProps, 'name' | 'boundaryIsOptimizedTerritoriesTrial'>
  & {
    readonly isAiGenerated: boolean;
    readonly boundaryPermissions: BoundaryGroupPermissions;
    readonly className?: string;
    readonly maxNumberOfLines?: number;
  };

export const ActiveBoundaryHeaderComponent: FC<ActiveBoundaryHeaderProps> = ({
  name,
  className,
  isAiGenerated,
  boundaryPermissions,
  maxNumberOfLines = 3,
  boundaryIsOptimizedTerritoriesTrial,
}) => {
  const [t] = useTranslation();

  return (
    <div
      css={wrapperStyle}
      className={className}
    >
      <div css={primaryHeaderStyle}>
        <LimitNumberOfLinesComponent maxNumberOfLines={maxNumberOfLines}>
          {name}
        </LimitNumberOfLinesComponent>
        <div css={subTitleStyle}>
          <div css={groupTypeStyle}>
            {isAiGenerated && (
              <FontAwesomeIcon
                icon={faRobot}
              />
            )} {boundaryPermissions.boundaryIsCustom ? t('territory group') : t('boundary')}
          </div>
          {boundaryIsOptimizedTerritoriesTrial && (
            <TrialModeWarningComponent />
          )}
        </div>
      </div>
    </div>
  );
};
